"use strict";
import { NetworkStatus } from "@apollo/client";
import { usePendingActivity } from "components/AccountDrawer/MiniPortfolio/Activity/hooks";
import { AdaptiveTokenBalancesProvider } from "graphql/data/apollo/AdaptiveTokenBalancesProvider";
import { apolloClient } from "graphql/data/apollo/client";
import { useAccount } from "hooks/useAccount";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useWatchTransactionsCallback } from "state/sagas/transactions/watcherSaga";
import { usePendingTransactions } from "state/transactions/hooks";
import {
  PortfolioBalancesDocument
} from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { useEnabledChains } from "features/chains/hooks/useEnabledChains";
import { usePortfolioValueModifiers } from "features/dataApi/balances";
import { usePrevious } from "utilities/src/react/hooks";
function useHasAccountUpdate() {
  const { pendingActivityCount } = usePendingActivity();
  const prevPendingActivityCount = usePrevious(pendingActivityCount);
  const hasLocalStateUpdate = !!prevPendingActivityCount && pendingActivityCount < prevPendingActivityCount;
  const account = useAccount();
  const prevAccount = usePrevious(account.address);
  const { isTestnetModeEnabled } = useEnabledChains();
  const prevIsTestnetModeEnabled = usePrevious(isTestnetModeEnabled);
  return useMemo(() => {
    const hasPolledTxUpdate = hasLocalStateUpdate;
    const accountChanged = Boolean(prevAccount !== account.address && account.address);
    const hasTestnetModeChanged = prevIsTestnetModeEnabled !== isTestnetModeEnabled;
    return hasPolledTxUpdate || accountChanged || hasTestnetModeChanged;
  }, [account.address, hasLocalStateUpdate, prevAccount, prevIsTestnetModeEnabled, isTestnetModeEnabled]);
}
function TokenBalancesProviderInternal({ children }) {
  const account = useAccount();
  const hasAccountUpdate = useHasAccountUpdate();
  const valueModifiers = usePortfolioValueModifiers(account.address);
  const prevValueModifiers = usePrevious(valueModifiers);
  const pendingTransactions = usePendingTransactions();
  const prevPendingTransactions = usePrevious(pendingTransactions);
  const pendingDiff = useMemo(
    () => prevPendingTransactions?.filter((tx) => !pendingTransactions.includes(tx)),
    [pendingTransactions, prevPendingTransactions]
  );
  const watchTransactions = useWatchTransactionsCallback();
  useEffect(() => {
    if (!account.address || !account.chainId) {
      return;
    }
    if (!pendingDiff?.length) {
      return;
    }
    watchTransactions({
      address: account.address,
      chainId: account.chainId,
      pendingDiff,
      apolloClient
    });
  }, [pendingDiff, account.address, account.chainId, watchTransactions]);
  const fetch = useCallback(() => {
  }, []);
  const mockQuery = useMemo(() => {
    const observable = apolloClient.watchQuery({
      query: PortfolioBalancesDocument,
      fetchPolicy: "cache-only"
    });
    return {
      data: void 0,
      previousData: void 0,
      error: void 0,
      loading: false,
      networkStatus: NetworkStatus.ready,
      called: true,
      client: apolloClient,
      observable,
      variables: observable.variables,
      refetch: () => observable.refetch(),
      fetchMore: (options) => observable.fetchMore(options),
      subscribeToMore: (options) => observable.subscribeToMore(options),
      updateQuery: (mapFn) => observable.updateQuery(mapFn),
      startPolling: (pollInterval) => observable.startPolling(pollInterval),
      stopPolling: () => observable.stopPolling(),
      reobserve: (newOptions, newNetworkStatus) => observable.reobserve(newOptions, newNetworkStatus)
    };
  }, []);
  return <AdaptiveTokenBalancesProvider
    query={mockQuery}
    fetch={fetch}
    stale={hasAccountUpdate || valueModifiers !== prevValueModifiers}
  >
      {children}
    </AdaptiveTokenBalancesProvider>;
}
export function TokenBalancesProvider({ children }) {
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    setInitialized(true);
  }, []);
  if (!initialized) {
    return null;
  }
  return <TokenBalancesProviderInternal>{children}</TokenBalancesProviderInternal>;
}
